import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Warning, ArrowClockwise, FolderSimplePlus, Broom, Sparkle, Spinner } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { dmsApi } from './dmsApi';

// 書類の片付け（D4 読取の止まり / D5 迷子の書類 / D6b 索引の余り / D6c 意味検索）。
// どれも「今どうなっているか」を数で出し、直す操作は人が押したときだけ動く。
const Row = ({ label, value, tone }) => (
  <div className="flex items-baseline justify-between gap-3 py-1">
    <span className="text-xs text-zinc-500">{label}</span>
    <span className={`text-sm font-semibold tabular-nums ${tone || 'text-zinc-800'}`}>{value}</span>
  </div>
);

const Card = ({ title, icon: Icon, children, testid }) => (
  <section className="rounded-lg border border-zinc-200 bg-white p-3" data-testid={testid}>
    <h3 className="flex items-center gap-1.5 text-sm font-semibold text-zinc-800 mb-1.5">
      <Icon size={15} weight="duotone" className="text-teal-600" /> {title}
    </h3>
    {children}
  </section>
);

export default function DocsHealthCard() {
  const [ocr, setOcr] = useState(null);
  const [orphans, setOrphans] = useState(null);
  const [index, setIndex] = useState(null);
  const [sem, setSem] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    dmsApi.ocrBacklog().then(setOcr).catch(() => {});
    dmsApi.orphans().then(setOrphans).catch(() => {});
    dmsApi.indexHealth().then(setIndex).catch(() => {});
    dmsApi.semanticStatus().then(setSem).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const r = await fn();
      toast.success(okMsg(r));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '実行できませんでした');
    } finally { setBusy(''); }
  };

  return (
    <div className="space-y-3" data-testid="dms-health-cards">
      <Card title="文字読み取り（OCR）の待ち" icon={Warning} testid="dms-ocr-backlog-card">
        <Row label="読取待ち" value={ocr?.total ?? '—'} />
        <Row label="1時間以上待っている" value={ocr?.stuck_over_1h ?? '—'}
          tone={(ocr?.stuck_over_1h || 0) > 0 ? 'text-rose-600' : undefined} />
        {(ocr?.stuck_over_1h || 0) > 0 && (
          <p className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800"
            data-testid="dms-ocr-stuck-warning">{ocr.notice_ja}</p>
        )}
        <ul className="mt-1.5 space-y-0.5 max-h-28 overflow-y-auto">
          {(ocr?.rows || []).slice(0, 8).map((r) => (
            <li key={r.document_id} className="flex items-center gap-2 text-[11px] text-zinc-600">
              <span className="truncate flex-1">{r.title}</span>
              <span className="text-zinc-400 tabular-nums shrink-0">{r.waiting_minutes ?? '—'}分</span>
              <button type="button" onClick={() => run(`ocr-${r.document_id}`,
                () => dmsApi.runOcr(r.document_id), () => '再読取を始めました')}
                data-testid={`dms-ocr-retry-${r.document_id}`}
                className="shrink-0 rounded border border-zinc-200 px-1.5 py-0.5 text-[10px] text-teal-700 hover:bg-teal-50">
                再読取
              </button>
            </li>
          ))}
        </ul>
        <Button size="sm" variant="outline" className="mt-2 h-8 text-xs"
          disabled={busy === 'requeue' || !(ocr?.stuck_over_1h)}
          onClick={() => run('requeue', () => dmsApi.ocrRequeue({ only_stuck: true }),
            (r) => `${r.queued}件を読取待ちに並べ直しました`)}
          data-testid="dms-ocr-requeue-btn">
          {busy === 'requeue' ? <Spinner size={13} className="animate-spin mr-1" /> : <ArrowClockwise size={13} className="mr-1" />}
          止まっている分を並べ直す
        </Button>
      </Card>

      <Card title="置き場所が無い書類" icon={FolderSimplePlus} testid="dms-orphans-card">
        <Row label="フォルダー無し" value={orphans?.no_folder_count ?? '—'}
          tone={(orphans?.no_folder_count || 0) > 0 ? 'text-rose-600' : undefined} />
        <Row label="書類番号無し" value={orphans?.no_number_count ?? '—'}
          tone={(orphans?.no_number_count || 0) > 0 ? 'text-rose-600' : undefined} />
        <ul className="mt-1 space-y-0.5 max-h-20 overflow-y-auto">
          {/* L3 — フォルダー無しと番号無しの両方に出る書類があり、同じ key が
              2回使われていた。書類IDで重複を取り除く。 */}
          {Object.values(Object.fromEntries(
            [...(orphans?.no_folder || []), ...(orphans?.no_number || [])]
              .map((r) => [r.document_id, r]))).slice(0, 6).map((r) => (
                <li key={r.document_id} className="truncate text-[11px] text-zinc-500">
                  {r.title}{!r.document_number && <span className="ml-1 text-rose-500">（番号無し）</span>}
                </li>
          ))}
        </ul>
        <Button size="sm" variant="outline" className="mt-2 h-8 text-xs"
          disabled={busy === 'organize' || !(orphans?.total)}
          onClick={() => run('organize', () => dmsApi.organizeOrphans({}),
            (r) => `「未整理」へ ${r.filed}件、番号を ${r.numbered}件 付けました`)}
          data-testid="dms-organize-orphans-btn">
          「未整理」に置いて番号を付ける
        </Button>
      </Card>

      <Card title="検索索引" icon={Broom} testid="dms-index-card">
        <Row label="索引の行" value={index?.indexed ?? '—'} />
        <Row label="有効な書類" value={index?.active_documents ?? '—'} />
        <Row label="もう無い書類の行" value={index?.stale_count ?? '—'}
          tone={(index?.stale_count || 0) > 0 ? 'text-rose-600' : undefined} />
        {index?.notice_ja && <p className="text-[11px] text-zinc-500 mt-1">{index.notice_ja}</p>}
        <Button size="sm" variant="outline" className="mt-2 h-8 text-xs"
          disabled={busy === 'prune' || !(index?.stale_count)}
          onClick={() => run('prune', () => dmsApi.indexPrune(), (r) => `索引から ${r.removed}行 取り除きました`)}
          data-testid="dms-index-prune-btn">
          索引を整える（書類は消えません）
        </Button>
      </Card>

      <Card title="意味検索（AI検索）" icon={Sparkle} testid="dms-semantic-card">
        {sem && !sem.configured ? (
          <div data-testid="dms-semantic-not-configured">
            <p className="text-sm font-semibold text-amber-700">意味検索は未設定</p>
            <ul className="mt-1 list-disc pl-4 text-[11px] text-zinc-600">
              {(sem.missing || []).map((m) => <li key={m}>{m}</li>)}
            </ul>
            <p className="mt-1 text-[11px] text-zinc-500">{sem.setup_note_ja}</p>
          </div>
        ) : (
          <>
            <Row label="索引された書類" value={sem?.indexed_documents ?? '—'} />
            <Row label="ベクトルあり" value={sem?.with_vectors ?? '—'}
              tone={(sem?.with_vectors || 0) === 0 ? 'text-amber-600' : 'text-emerald-700'} />
            <p className="text-[11px] text-zinc-500 mt-1">
              ローカルLLM {sem?.endpoint_host || '—'} / {sem?.model || '—'}
              {sem?.vector_dim ? `（${sem.vector_dim}次元）` : ''}
            </p>
          </>
        )}
        <Button size="sm" variant="outline" className="mt-2 h-8 text-xs" disabled={busy === 'sem'}
          onClick={() => run('sem', () => dmsApi.semanticBuild(),
            (r) => `${r.built}件のベクトルを作りました`)}
          data-testid="dms-semantic-build-btn">
          {busy === 'sem' ? <Spinner size={13} className="animate-spin mr-1" /> : null}
          意味検索を準備する（有効な書類のみ）
        </Button>
      </Card>
    </div>
  );
}
